import { Loader } from "@circle-react-uikit/Loader";
import { Row } from "@circle-react-uikit/SkeletonLoader/components/Row";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { useLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";

import { CheckoutForm } from ".";
import { usePayment } from "../../hooks/usePayment";
import { CheckoutDescription } from "../CheckoutDescription";
import { PaywallCustomization } from "../CheckoutDescription/PaywallCustomization";
import { Column } from "../components/Column";
import { CheckoutFormAccountInfo } from "./CheckoutFormAccountInfo";
import { CheckoutFormSection } from "./CheckoutFormSection";
import { CheckoutFormTaxIdField } from "./CheckoutFormTaxIdField";
import { CheckoutFormTOS } from "./CheckoutFormTOS";
import { BuyButton } from "./components/BuyButton";
import { Elements } from "./StripeElements";
import { useCheckoutSubmit } from "./useCheckoutSubmit";

interface RootProps {
  className?: string;
}

export const Root = Object.assign(
  ({ className }: RootProps) => {
    const formMethods = useFormContext();
    const { handleSubmit } = formMethods;
    const {
      isProcessingPayment,
      authProviderProperties,
      checkoutPreviewContext,
      currentCommunity,
    } = usePaywallCheckoutContext();

    const { payButtonLabel, stripe } = usePayment();
    const { submit, isSubmitDisabled } = useCheckoutSubmit();
    const isLgOrLargerScreen = useLgScreenMediaQuery();

    if (authProviderProperties?.is_sso_authentication_required) {
      return null;
    }

    return (
      <Column
        className={classNames(
          "flex h-auto w-full flex-col items-center lg:items-start",
          className,
          {
            "pb-6": !!checkoutPreviewContext,
          },
        )}
        dataTestId="paywall-checkout-right-column"
      >
        <CheckoutDescription.CommunityHeader
          className="hidden lg:invisible lg:block"
          community={currentCommunity}
        />
        {isProcessingPayment && (
          <Loader
            variant="fullscreen"
            message={t("paywalls.checkout.bnpl_processing")}
          />
        )}
        <form
          className="flex flex-col gap-y-6"
          onSubmit={handleSubmit(submit)}
          data-testid="checkout-form"
        >
          <CheckoutForm.Alerts className="hidden lg:block" shouldFocus />
          <CheckoutFormAccountInfo />
          <CheckoutFormSection className="gap-y-5">
            <Elements />
            <CheckoutFormTaxIdField />
          </CheckoutFormSection>
          <CheckoutForm.Alerts className="block lg:hidden" />
          <CheckoutFormSection className="gap-y-5">
            <CheckoutFormTOS />
            <BuyButton
              disabled={!stripe || isSubmitDisabled || isProcessingPayment}
              dataTestId="checkout-form-submit-button"
              label={payButtonLabel}
            />
          </CheckoutFormSection>
        </form>
        <PaywallCustomization shouldHide={isLgOrLargerScreen} />
      </Column>
    );
  },
  {
    Skeleton: ({ className }: RootProps) => (
      <Column
        className={classNames(
          "flex h-auto w-full flex-col items-center lg:items-start",
          className,
        )}
        dataTestId="paywall-checkout-right-column"
      >
        <Row
          square
          full
          x2l
          className="!h-[2.5rem] lg:!h-10 my-[1.625rem] md:my-[2.625rem] !w-full lg:!w-1/2 hidden lg:invisible lg:block"
        />
        <div className="flex flex-col gap-y-6">
          <CheckoutFormAccountInfo.Skeleton />
          <CheckoutFormSection className="gap-y-5">
            <Row square half xl />
            <div className="flex flex-col border-primary shadow-xs rounded-xl border bg-primary p-6 gap-y-4">
              <Row square full x2l />
              <Row full />
              <div className="w-full flex flex-col gap-y-2">
                <Row half />
                <Row square full x2l />
              </div>
              <div className="flex flex-row gap-x-3">
                <div className="w-full flex flex-col gap-y-2">
                  <Row half />
                  <Row square full x2l />
                </div>
                <div className="w-full flex flex-col gap-y-2">
                  <Row half />
                  <Row square full x2l />
                </div>
              </div>
            </div>
          </CheckoutFormSection>
          <CheckoutFormSection className="gap-y-2">
            <Row full />
            <Row square x2l full />
          </CheckoutFormSection>
        </div>
      </Column>
    ),
  },
);
